import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Video, MapPin, CheckCircle2, Clock, ShieldCheck, ShieldAlert, Ban, Calendar, MessageSquare, X, Repeat, ChevronDown } from "lucide-react";
import useModalA11y from "@/lib/useModalA11y";

const LDN = { timeZone: "Europe/London" };
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-GB", { ...LDN, day: "numeric", month: "short", year: "numeric" });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-GB", { ...LDN, hour: "2-digit", minute: "2-digit" });
const fmtWhen = (iso) => { try { return `${new Date(iso).toLocaleDateString("en-GB", { ...LDN, day: "numeric", month: "short" })}, ${fmtTime(iso)}`; } catch { return ""; } };

// Build a readable, chronological status timeline from the appointment's
// existing status_history (no duplicate storage). The opening "Requested"
// event comes from created_at; subsequent rows map each recorded transition.
function buildTimeline(a) {
  if (!a) return { events: [], reopened: false };
  const actor = (h) => (h.by_role === "patient" ? (a.patient_name || "patient")
    : h.by_role === "clinician" ? (a.clinician_name || "clinician")
    : h.by_role || "");
  const events = [];
  if (a.created_at) events.push({ label: "Requested", at: a.created_at, who: a.patient_name || "" });
  let reopened = false;
  for (const h of a.status_history || []) {
    const by = actor(h);
    if (h.to === "declined") events.push({ label: `Declined${by ? ` by ${by}` : ""}`, at: h.at });
    else if (h.to === "cancelled") events.push({ label: `Cancelled${by ? ` by ${by}` : ""}`, at: h.at });
    else if (h.to === "pending" && ["declined", "cancelled"].includes(h.from)) { reopened = true; events.push({ label: `Reopened${by ? ` by ${by}` : ""}`, at: h.at }); }
    else if (h.to === "confirmed") events.push({ label: "Confirmed", at: h.at });
    else if (h.to === "rescheduled") events.push({ label: `Rescheduled${by ? ` by ${by}` : ""}`, at: h.at });
    else if (h.to === "completed") events.push({ label: "Completed", at: h.at });
  }
  return { events, reopened };
}

// Batch 9D · completion is allowed only from an attended-session state whose
// start time has passed. Mirrors the server-side eligibility rule so the UI
// never offers an action the backend will reject. No time-based auto-complete.
const canComplete = (a) =>
  !!a && ["confirmed", "rescheduled"].includes(a.status) &&
  !!a.scheduled_at && new Date(a.scheduled_at) <= new Date();

const STATUS_META = {
  pending:      { label: "Pending",      bg: "rgba(245,158,11,.15)", fg: "#F59E0B", Icon: Clock },
  confirmed:    { label: "Confirmed",    bg: "rgba(34,197,94,.15)",  fg: "#22C55E", Icon: ShieldCheck },
  declined:     { label: "Declined",     bg: "rgba(239,68,68,.15)",  fg: "#EF4444", Icon: ShieldAlert },
  cancelled:    { label: "Cancelled",    bg: "rgba(239,68,68,.10)",  fg: "#EF4444", Icon: Ban },
  rescheduled:  { label: "Rescheduled",  bg: "rgba(59,130,246,.15)", fg: "#3B82F6", Icon: Calendar },
  completed:    { label: "Completed",    bg: "rgba(34,197,94,.12)",  fg: "#22C55E", Icon: CheckCircle2 },
};

export default function ClinicianSchedule() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  const openApptId = new URLSearchParams(location.search).get("appt");
  const [appts, setAppts] = useState([]);
  const [completing, setCompleting] = useState(null);
  const [detail, setDetail] = useState(null);
  const [rescheduleAt, setRescheduleAt] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [recurWeeks, setRecurWeeks] = useState(6);
  const [recurring, setRecurring] = useState(false);
  const [confirmDecline, setConfirmDecline] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const modalBodyRef = useRef(null);
  const detailRef = useModalA11y({ open: !!detail, onClose: () => setDetail(null) });
  const openedFromLinkRef = useRef(false);

  const closeModal = () => { setDetail(null); setConfirmDecline(false); setNote(""); setRescheduleAt(""); };

  const refresh = () => api.get("/appointments/mine").then((r) => setAppts(r.data));
  useEffect(() => { refresh(); }, []);

  // When a modal opens, always reset its internal scroll to the top so the
  // title/date/status are immediately visible (never an old scroll position).
  useEffect(() => {
    if (detail) {
      setConfirmDecline(false);
      const tl = buildTimeline(detail);
      // Expand by default only when there's more than one meaningful change.
      setHistoryOpen((tl.events.length - 1) > 1);
      requestAnimationFrame(() => { if (modalBodyRef.current) modalBodyRef.current.scrollTop = 0; });
    }
  }, [detail]);

  // Auto-open the modal when arriving from a notification `?appt=<id>`.
  useEffect(() => {
    if (!openApptId || openedFromLinkRef.current) return;
    const found = appts.find((a) => a.id === openApptId);
    if (found) { setDetail(found); openedFromLinkRef.current = true; }
  }, [openApptId, appts]);

  const markComplete = async (id, { closeOnSuccess = false } = {}) => {
    setCompleting(id);
    try {
      await api.post(`/appointments/${id}/complete`);
      toast.success("Session marked complete — the patient has been asked for feedback.");
      if (closeOnSuccess) closeModal();
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't complete session.");
    } finally { setCompleting(null); }
  };

  const updateStatus = async (status, extra = {}) => {
    if (!detail) return;
    setSaving(true);
    try {
      const body = { status, ...extra };
      if (note.trim()) body.note = note.trim();
      await api.patch(`/appointments/${detail.id}/status`, body);
      const msg = status === "pending" ? "Request reopened — now pending your decision"
        : status === "confirmed" ? "Appointment confirmed"
        : status === "declined" ? "Appointment declined"
        : status === "rescheduled" ? "New time proposed" : `Marked as ${status}`;
      toast.success(msg);
      refresh();
      closeModal();           // only closes AFTER a successful request
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    } finally { setSaving(false); }
  };

  const messagePatient = () => {
    if (!detail) return;
    const first = (detail.patient_name || "there").split(" ")[0];
    const body = `Hi ${first}, `;
    const to = detail.patient_id;
    closeModal();             // close the appointment modal, then open the thread
    nav(`/clinician/messages?to=${encodeURIComponent(to)}&prefill=${encodeURIComponent(body)}`);
  };

  const makeRecurring = async () => {
    if (!detail) return;
    setRecurring(true);
    try {
      const r = await api.post(`/appointments/${detail.id}/recur`, { weeks: Number(recurWeeks) || 6, status: "confirmed" });
      const created = r.data?.created?.length || 0;
      const skipped = r.data?.skipped?.length || 0;
      toast.success(`Booked ${created} sessions${skipped ? ` (${skipped} skipped)` : ""}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create the series");
    } finally { setRecurring(false); }
  };

  const today = new Date().toDateString();
  const grouped = useMemo(() => {
    const g = { today: [], upcoming: [], past: [] };
    appts.forEach((a) => {
      const d = new Date(a.scheduled_at);
      if (d.toDateString() === today) g.today.push(a);
      else if (d > new Date()) g.upcoming.push(a);
      else g.past.push(a);
    });
    return g;
  }, [appts, today]);

  return (
    <DashLayout>
      <div className="mb-8">
        <div className="fl-label text-[#f47a20]">{t("pages.cAppts.eyebrow")}</div>
        <h1 className="fl-heading text-4xl mt-2">{t("pages.cAppts.h1")}</h1>
      </div>
      {[["Today", grouped.today], ["Upcoming", grouped.upcoming], ["Past", grouped.past]].map(([label, list]) => (
        <div key={label} className="mb-8">
          <div className="fl-heading text-xl mb-3">{label} <span className="text-zinc-500 text-sm">({list.length})</span></div>
          <div className="space-y-2">
            {list.length === 0 && <div className="text-sm text-zinc-500">None.</div>}
            {list.map((a) => {
              const st = STATUS_META[a.status || "confirmed"] || STATUS_META.confirmed;
              const StIcon = st.Icon;
              return (
                <div key={a.id} className="fl-card p-4 flex items-center gap-4 flex-wrap md:flex-nowrap" data-testid={`appt-${a.id}`}>
                  {a.mode === "remote" ? <Video className="text-[#f47a20]" size={18} /> : <MapPin className="text-[#f47a20]" size={18} />}
                  <div className="flex-1 min-w-0">
                    <button onClick={() => setDetail(a)} className="text-left" data-testid={`appt-open-${a.id}`}>
                      <div className="fl-heading text-base truncate hover:text-[#f47a20] transition-colors">{a.patient_name}</div>
                      <div className="text-xs text-zinc-500 truncate">{a.service} · {a.duration_min} min</div>
                    </button>
                    <div className="mt-1 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full"
                         style={{ background: st.bg, color: st.fg, border: `1px solid ${st.fg}55` }}
                         data-testid={`appt-status-badge-${a.id}`}>
                      <StIcon size={10} /> {st.label}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm">{fmtDate(a.scheduled_at).split(" ").slice(0, 2).join(" ")}</div>
                    <div className="text-xs text-zinc-500">{fmtTime(a.scheduled_at)}</div>
                  </div>
                  {a.status === "completed" ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs flex items-center gap-1 px-2 py-1 rounded-full"
                            style={{ background: "rgba(34,197,94,.12)", color: "#22c55e", border: "1px solid rgba(34,197,94,.35)" }}
                            data-testid={`appt-completed-badge-${a.id}`}>
                        <CheckCircle2 size={12} /> Completed
                      </span>
                      <button
                        onClick={() => nav(`/clinician/patient/${a.patient_id}?tab=appointments`)}
                        className="text-xs px-3 py-1.5 rounded-md font-medium fl-btn-ghost"
                        data-testid={`appt-session-review-${a.id}`}
                      >Session review</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setDetail(a)}
                        className="text-xs px-3 py-1.5 rounded-md font-medium fl-btn-ghost"
                        data-testid={`appt-review-${a.id}`}
                      >Review</button>
                      {canComplete(a) && (
                        <button
                          onClick={() => markComplete(a.id)}
                          disabled={completing === a.id}
                          data-testid={`appt-complete-${a.id}`}
                          className="text-xs px-3 py-1.5 rounded-md font-medium transition-colors disabled:opacity-50"
                          style={{ background: "color-mix(in oklab, var(--fl-orange) 15%, transparent)",
                                   color: "var(--fl-orange)",
                                   border: "1px solid color-mix(in oklab, var(--fl-orange) 40%, transparent)" }}
                        >
                          {completing === a.id ? "…" : "Complete"}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {detail && (() => {
        const st = STATUS_META[detail.status || "confirmed"] || STATUS_META.confirmed;
        const StIcon = st.Icon;
        const isClosed = ["declined", "cancelled"].includes(detail.status);
        const timeline = buildTimeline(detail);
        return (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-0 sm:p-4" onClick={closeModal}>
            <div ref={detailRef} role="dialog" aria-modal="true"
                 className="fl-card fl-card-elevated w-full sm:max-w-lg flex flex-col overflow-hidden rounded-none sm:rounded-2xl"
                 style={{ maxHeight: "100dvh", height: "auto" }}
                 onClick={(e) => e.stopPropagation()}
                 data-testid="clin-appt-detail-modal">
              {/* Sticky header — always visible */}
              <div className="flex items-start justify-between gap-4 p-5 sm:p-6 border-b border-white/10 shrink-0" style={{ paddingTop: "max(1.25rem, env(safe-area-inset-top))" }}>
                <div>
                  <div className="fl-label text-[#f47a20]">Booking</div>
                  <div className="fl-heading text-2xl mt-1">{detail.patient_name}</div>
                  <div className="text-xs text-zinc-500 mt-1">{detail.service}</div>
                </div>
                <button onClick={closeModal} aria-label="Close" data-testid="clin-appt-detail-close"><X size={16} className="text-zinc-400"/></button>
              </div>

              {/* Scrollable body */}
              <div ref={modalBodyRef} className="p-5 sm:p-6 overflow-y-auto grow" data-testid="clin-appt-modal-body">
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="fl-card p-3">
                  <div className="fl-label mb-1">When (London)</div>
                  <div className="text-sm">{fmtDate(detail.scheduled_at)}</div>
                  <div className="text-sm text-[#f47a20]">{fmtTime(detail.scheduled_at)} · {detail.duration_min} min</div>
                </div>
                <div className="fl-card p-3">
                  <div className="fl-label mb-1">Mode</div>
                  <div className="text-sm capitalize">{(detail.mode || "in_person").replace("_", " ")}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap mb-4">
                <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full"
                     style={{ background: st.bg, color: st.fg, border: `1px solid ${st.fg}55` }}
                     data-testid="clin-appt-detail-status">
                  <StIcon size={11} /> {st.label}
                </div>
                {timeline.reopened && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full"
                        style={{ background: "rgba(59,130,246,.15)", color: "#3B82F6", border: "1px solid rgba(59,130,246,.4)" }}
                        data-testid="clin-appt-reopened-tag">
                    <Repeat size={10} /> Reopened
                  </span>
                )}
              </div>

              {timeline.events.length > 0 && (
                <div className="mb-4" data-testid="clin-appt-history">
                  <button type="button" onClick={() => setHistoryOpen((v) => !v)}
                          className="w-full flex items-center justify-between text-xs text-zinc-400 hover:text-white py-1"
                          data-testid="clin-appt-history-toggle">
                    <span className="fl-label">Appointment history</span>
                    <ChevronDown size={14} className={`transition-transform ${historyOpen ? "rotate-180" : ""}`} />
                  </button>
                  {historyOpen && (
                    <ol className="mt-2 border-l border-white/10 pl-4 space-y-2" data-testid="clin-appt-history-list">
                      {timeline.events.map((e, i) => (
                        <li key={i} className="relative text-xs">
                          <span className="absolute -left-[21px] top-1 w-1.5 h-1.5 rounded-full" style={{ background: i === timeline.events.length - 1 ? "var(--fl-orange)" : "#52525b" }} />
                          <span className="text-zinc-200">{e.label}</span>
                          <span className="text-zinc-500"> — {fmtWhen(e.at)}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}

              <div className="mb-3">
                <div className="fl-label mb-1">Optional note (visible to the patient)</div>
                <textarea className="fl-input min-h-[64px]" rows={2}
                          value={note} onChange={(e) => setNote(e.target.value)}
                          placeholder="e.g. Please arrive 5 min early…"
                          data-testid="clin-appt-note" />
              </div>

              {isClosed ? (
                /* ---- Recovery: a mistaken decline/cancel can be reopened ---- */
                <div className="flex flex-col gap-2" data-testid="clin-appt-recovery">
                  <div className="fl-card p-3 text-xs" style={{ color: "var(--fl-muted)", border: "1px solid rgba(239,68,68,.35)" }}>
                    This request is {detail.status}. You can reopen it to pending if this was a mistake — the patient will be notified again only when you confirm or reschedule.
                  </div>
                  <button onClick={() => updateStatus("pending")} disabled={saving}
                          className="fl-btn w-full justify-center" data-testid="clin-appt-reopen">
                    <Repeat size={14} /> Reopen request
                  </button>
                  <button onClick={messagePatient} className="fl-btn-ghost text-xs w-full justify-center" data-testid="clin-appt-message">
                    <MessageSquare size={12} /> Message patient
                  </button>
                </div>
              ) : confirmDecline ? (
                /* ---- Decline confirmation step ---- */
                <div className="flex flex-col gap-2" data-testid="clin-appt-decline-confirm">
                  <div className="fl-card p-3 text-sm" style={{ border: "1px solid rgba(239,68,68,.45)" }}>
                    <div className="fl-heading text-base mb-1">Decline appointment?</div>
                    <div className="text-xs text-zinc-400">The patient will be notified that this appointment request was declined. You can reopen it later from History if needed.</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setConfirmDecline(false)} disabled={saving}
                            className="fl-btn-ghost text-sm flex-1 justify-center" data-testid="clin-appt-decline-cancel">Keep appointment</button>
                    <button onClick={() => updateStatus("declined")} disabled={saving}
                            className="text-sm flex-1 justify-center inline-flex items-center gap-1.5 px-4 py-2 rounded-md font-semibold"
                            style={{ background: "#EF4444", color: "#fff" }}
                            data-testid="clin-appt-decline-confirmed">
                      <ShieldAlert size={14} /> Decline appointment
                    </button>
                  </div>
                </div>
              ) : (
                /* ---- Primary action area (clear hierarchy) ---- */
                <div className="flex flex-col gap-3">
                  {canComplete(detail) && (
                    <button onClick={() => markComplete(detail.id, { closeOnSuccess: true })}
                            disabled={saving || completing === detail.id}
                            className="w-full justify-center inline-flex items-center gap-1.5 px-4 py-2 rounded-md font-semibold"
                            style={{ background: "color-mix(in oklab, var(--fl-orange) 18%, transparent)",
                                     color: "var(--fl-orange)",
                                     border: "1px solid color-mix(in oklab, var(--fl-orange) 45%, transparent)" }}
                            data-testid="clin-appt-mark-completed">
                      <CheckCircle2 size={14} /> {completing === detail.id ? "Marking…" : "Mark as completed"}
                    </button>
                  )}
                  <button onClick={() => updateStatus("confirmed")} disabled={saving}
                          className="fl-btn w-full justify-center" data-testid="clin-appt-confirm">
                    <ShieldCheck size={14} /> Confirm appointment
                  </button>
                  <div className="fl-card p-3">
                    <div className="fl-label mb-2">Propose a new time</div>
                    <input type="datetime-local" className="fl-input"
                           value={rescheduleAt}
                           onChange={(e) => setRescheduleAt(e.target.value)}
                           data-testid="clin-appt-reschedule-at" />
                    <button
                      onClick={() => {
                        if (!rescheduleAt) return toast.error("Pick a new time");
                        const iso = new Date(rescheduleAt).toISOString();
                        updateStatus("rescheduled", { scheduled_at: iso });
                      }}
                      disabled={saving || !rescheduleAt}
                      className="fl-btn-ghost text-xs mt-2 w-full justify-center"
                      data-testid="clin-appt-reschedule-submit"
                    >
                      <Calendar size={12} /> Reschedule
                    </button>
                  </div>
                  <button onClick={messagePatient}
                          className="fl-btn-ghost text-sm w-full justify-center" data-testid="clin-appt-message">
                    <MessageSquare size={14} /> Message patient
                  </button>
                  <div className="fl-card p-3">
                    <div className="fl-label mb-2">Turn into a weekly plan</div>
                    <div className="flex items-center gap-2">
                      <input type="number" min={1} max={26} value={recurWeeks}
                             onChange={(e) => setRecurWeeks(e.target.value)}
                             className="fl-input w-20 text-center" data-testid="clin-appt-recur-weeks" />
                      <span className="text-xs" style={{ color: "var(--fl-muted)" }}>weeks</span>
                      <button onClick={makeRecurring} disabled={recurring}
                              className="fl-btn-ghost text-xs flex-1 justify-center"
                              data-testid="clin-appt-recur-submit">
                        <Repeat size={12} /> {recurring ? "Booking…" : "Repeat weekly"}
                      </button>
                    </div>
                  </div>
                  <button onClick={() => setConfirmDecline(true)} disabled={saving}
                          className="text-xs inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md font-medium w-full"
                          style={{ border: "1px solid rgba(239,68,68,.45)", color: "#EF4444" }}
                          data-testid="clin-appt-decline">
                    <ShieldAlert size={12} /> Decline appointment
                  </button>
                </div>
              )}
              </div>
            </div>
          </div>
        );
      })()}
    </DashLayout>
  );
}
